import React from 'react';
import { Card, CardContent } from './ui/card';
import { TreePine, BarChart3, Sprout, Users, AlertTriangle } from 'lucide-react';
import { useRoleFlags } from '@/auth/useRoleFlags';

const fmt = (n) => (n != null && !Number.isNaN(n) ? Number(n).toLocaleString('tr-TR') : '0');

const defaultDisplayStats = {
  totalParks: 0, totalArea: 0, totalGrassArea: 0, totalRegions: 0,
  totalPersonnel: 0, activePersonnel: 0, onLeavePersonnel: 0, onReportPersonnel: 0,
  assignedParks: 0, unassignedParks: 0, parksWithRegion: 0, parksWithoutRegion: 0,
  unassignedParksList: []
};

const DashboardStatisticsCards = ({
  displayStats: displayStatsProp,
  userRole,
  personnelId,
  leaveRequests,
  activeSearchTerm,
  onClearSearch,
  setOnLeaveDialogOpen,
  setOnReportDialogOpen,
  setLowLeaveDialogOpen,
  setActiveTab,
  onParkFilterClick,
  onUnassignedParkGorevlisiClick,
  // Ek kartlar (Aktif Vekalet, Engellenenler…) ayrı bir satırda değil, BU
  // ızgaranın son hücresinde durur. Ayrı satırdayken kart dar olduğu için
  // satırın kalanı boş kalıyor, sayfa gereksiz uzuyordu.
  children
}) => {
  // Parklar sekmesindeki arama filtresi aktifse, "Toplam Park" kartında görünür yap —
  // sayılar bu filtreye duyarlı (filteredParks → displayedParks → stats), o yüzden
  // kullanıcı hangi metinle daraldığını görmeli.
  const activeFilterLabel = (activeSearchTerm || "").trim();
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const displayStats = displayStatsProp ?? defaultDisplayStats;
  // Sütun sayısı hücre sayısına göre seçilir ki son satırda boşluk kalmasın:
  // 5 kart + ek hücre = 6 → 1/2/3/6 sütunun hepsinde satırlar tam dolar.
  const hasExtras = React.Children.count(children) > 0;
  const colClasses = hasExtras
    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 min-[1700px]:grid-cols-6"
    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-5";
  return (
    <div className={`grid ${colClasses} gap-4`}>
      {/* Park İstatistikleri */}
      <Card className="flex flex-col bg-gradient-to-br from-green-500 to-green-600 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
        {/* Alt kırılımı olmayan kartlar satırın yüksekliğine uzuyor; içerik
            dikeyde ortalanınca altta ölü boşluk kalmıyor. */}
        <CardContent className="flex-1 flex items-center p-4">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-green-50 text-[13px] font-medium">Toplam Park</p>
              <h3 className="text-2xl font-bold mt-0.5">{fmt(displayStats.totalParks)}</h3>
              {activeFilterLabel && (
                <span
                  title={`Parklar sekmesindeki arama filtresi aktif: "${activeFilterLabel}"`}
                  className="inline-flex items-center gap-1 mt-2 pl-2 pr-1 py-0.5 rounded-full bg-white/20 text-green-50 text-[11px] font-medium max-w-full"
                >
                  <span className="truncate">🔍 {activeFilterLabel}</span>
                  {onClearSearch && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onClearSearch(); }}
                      title="Aramayı temizle"
                      aria-label="Aramayı temizle"
                      className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/30 transition-colors leading-none"
                    >
                      ×
                    </button>
                  )}
                </span>
              )}
            </div>
            <TreePine className="h-7 w-7 opacity-80 shrink-0" />
          </div>
        </CardContent>
      </Card>

      <Card className="flex flex-col bg-gradient-to-br from-blue-500 to-blue-600 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
        <CardContent className="flex-1 flex items-center p-4">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-blue-50 text-[13px] font-medium">Toplam Alan</p>
              <h3 className="text-2xl font-bold mt-0.5">{fmt(displayStats.totalArea)} m²</h3>
            </div>
            <BarChart3 className="h-7 w-7 opacity-80 shrink-0" />
          </div>
        </CardContent>
      </Card>

      <Card className="flex flex-col bg-gradient-to-br from-emerald-500 to-emerald-600 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
        <CardContent className="flex-1 flex items-center p-4">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-emerald-50 text-[13px] font-medium">Çim Alanı</p>
              <h3 className="text-2xl font-bold mt-0.5">{fmt(displayStats.totalGrassArea)} m²</h3>
            </div>
            <Sprout className="h-7 w-7 opacity-80 shrink-0" />
          </div>
        </CardContent>
      </Card>

      {/* Bölgeler İstatistikleri */}
      <Card
        className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
        onClick={() => setActiveTab && setActiveTab("regions")}
        /* "Tıklayarak görüntüle →" satırı kaldırıldı; tıklanabilirlik imleç ve
           bu ipucu ile korunuyor. */
        title="Bölgeleri görüntüle"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0">
              <p className="text-cyan-50 text-[13px] font-medium">Toplam Bölge</p>
              <h3 className="text-2xl font-bold mt-0.5">{fmt(displayStats.totalRegions)}</h3>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 opacity-80 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
          </div>
          <div className="border-t border-cyan-400 pt-2 grid grid-cols-2 gap-2">
            <div 
              className="bg-cyan-600/50 rounded px-2 py-1.5 hover:bg-cyan-600/70 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onParkFilterClick && onParkFilterClick("assigned");
              }}
            >
              <div className="flex items-baseline justify-between gap-1">
                <p className="text-cyan-50 text-xs font-medium truncate">Park Görevlisi Var</p>
                <p className="text-lg font-bold">{fmt(displayStats.assignedParks ?? displayStats.parksWithRegion)}</p>
              </div>
            </div>
            <div 
              className={`bg-cyan-600/50 rounded px-2 py-1.5 transition-colors ${(displayStats.unassignedParks ?? displayStats.parksWithoutRegion) > 0 ? 'cursor-pointer hover:bg-cyan-600/70' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if ((displayStats.unassignedParks ?? displayStats.parksWithoutRegion) > 0 && onUnassignedParkGorevlisiClick) {
                  onUnassignedParkGorevlisiClick();
                }
              }}
            >
              <div className="flex items-baseline justify-between gap-1">
                <p className="text-cyan-50 text-xs font-medium truncate">Park Görevlisi Yok</p>
                <p className="text-lg font-bold">{fmt(displayStats.unassignedParks ?? displayStats.parksWithoutRegion)}</p>
              </div>
              {(displayStats.unassignedParks ?? displayStats.parksWithoutRegion) > 0 && (
                <p className="text-cyan-50 text-[11px] leading-tight">
                  {has("personel.ata") ? '👆 Tıkla ve ata' : '👆 Tıkla ve görüntüle'}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 relative">
        {/* İzin bildirimleri kaldırıldı - İzin Yönetimi sekmesinde zaten gösteriliyor */}
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0">
              <p className="text-purple-50 text-[13px] font-medium">Toplam Personel</p>
              <h3 className="text-2xl font-bold mt-0.5">{fmt(displayStats.totalPersonnel)}</h3>
            </div>
            <Users className="h-7 w-7 opacity-80 shrink-0" />
          </div>
          <div className="border-t border-purple-400 pt-2 grid grid-cols-3 gap-2">
            <div className="bg-purple-600/50 rounded px-2 py-1.5">
              <p className="text-purple-50 text-xs font-medium truncate">Çalışan</p>
              <p className="text-lg font-bold">{fmt(displayStats.activePersonnel)}</p>
            </div>
            <div
              className="bg-purple-600/50 rounded px-2 py-1.5 cursor-pointer hover:bg-purple-500/70 transition-colors"
              onClick={() => setOnLeaveDialogOpen(true)}
            >
              <p className="text-purple-50 text-xs font-medium truncate">İzinli</p>
              <p className="text-lg font-bold">{fmt(displayStats.onLeavePersonnel)}</p>
            </div>
            <div
              className="bg-purple-600/50 rounded px-2 py-1.5 cursor-pointer hover:bg-purple-500/70 transition-colors"
              onClick={() => setOnReportDialogOpen(true)}
            >
              <p className="text-purple-50 text-xs font-medium truncate">Raporlu</p>
              <p className="text-lg font-bold">{fmt(displayStats.onReportPersonnel)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {children}
    </div>
  );
};

export default DashboardStatisticsCards;
